import {
  Box,
  Button,
  Spinner,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  Select,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  useDisclosure,
  Checkbox,
  HStack,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import TrackingPageLayout from "components/TrackingPageLayout";
import { useState, useCallback } from "react";
import { useParentInvoices } from "hooks/useTrackingPayments";
import type { Invoice } from "hooks/useTrackingPayments";
import { showToast, toastTypes } from "utils";

const PAYMENT_METHODS = [
  { value: "orange_money", label: "Orange Money" },
  { value: "mtn_money", label: "MTN Money" },
  { value: "moov_money", label: "Moov Money" },
  { value: "cash", label: "Espèces" },
  { value: "bank_transfer", label: "Virement bancaire" },
  { value: "check", label: "Chèque" },
  { value: "other", label: "Autre" },
];

function formatDate(s: string | null | undefined) {
  if (!s) return "—";
  return new Date(s).toLocaleDateString("fr-FR");
}

function formatAmount(value: number | string | undefined) {
  if (value == null) return "—";
  const n = typeof value === "string" ? parseFloat(value) : value;
  return Number.isNaN(n) ? "—" : `${n.toFixed(2)} FCFA`;
}

function getRemainingNumber(inv: Invoice): number {
  const v = inv.remaining_amount;
  if (v == null) return 0;
  const n = typeof v === "string" ? parseFloat(v) : v;
  return Number.isNaN(n) ? 0 : n;
}

function isPayable(inv: Invoice): boolean {
  const s = (inv.status || "").toLowerCase();
  return s === "pending" || s === "partially_paid" || s === "overdue";
}

export default function TrackingParentInvoicesPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [payingInvoice, setPayingInvoice] = useState<Invoice | null>(null);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState("orange_money");
  const [payTransactionId, setPayTransactionId] = useState("");
  const [payNotes, setPayNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const {
    invoices,
    isLoading,
    error,
    refetch,
    payInvoice,
    bulkPayInvoices,
  } = useParentInvoices(statusFilter ? { status: statusFilter } : undefined);

  const singlePayModal = useDisclosure();
  const bulkPayModal = useDisclosure();

  const openSinglePay = useCallback(
    (inv: Invoice) => {
      setPayingInvoice(inv);
      setPayAmount(String(getRemainingNumber(inv) || ""));
      setPayMethod("orange_money");
      setPayTransactionId("");
      setPayNotes("");
      singlePayModal.onOpen();
    },
    [singlePayModal]
  );

  const closeSinglePay = useCallback(() => {
    singlePayModal.onClose();
    setPayingInvoice(null);
  }, [singlePayModal]);

  const handleSinglePay = useCallback(async () => {
    if (!payingInvoice) return;
    const amount = parseFloat(payAmount);
    if (Number.isNaN(amount) || amount <= 0) {
      showToast({ toast, message: "Montant invalide", status: toastTypes.error });
      return;
    }
    setSubmitting(true);
    try {
      await payInvoice(payingInvoice.id, {
        amount,
        method: payMethod,
        transaction_id: payTransactionId.trim() || undefined,
        notes: payNotes.trim() || undefined,
      });
      showToast({ toast, message: "Paiement enregistré avec succès.", status: toastTypes.success });
      closeSinglePay();
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.detail ||
        e?.message ||
        "Erreur lors du paiement.";
      showToast({ toast, message: msg, status: toastTypes.error });
    } finally {
      setSubmitting(false);
    }
  }, [payingInvoice, payAmount, payMethod, payTransactionId, payNotes, payInvoice, toast, closeSinglePay]);

  const selectedInvoices = invoices.filter((inv: Invoice) => selectedIds.has(inv.id));
  const totalSelectedRemaining = selectedInvoices.reduce(
    (sum: number, inv: Invoice) => sum + getRemainingNumber(inv),
    0
  );
  const [bulkAmount, setBulkAmount] = useState("");
  const [bulkMethod, setBulkMethod] = useState("orange_money");
  const [bulkTransactionId, setBulkTransactionId] = useState("");
  const [bulkNotes, setBulkNotes] = useState("");

  const openBulkPay = useCallback(() => {
    setBulkAmount(String(totalSelectedRemaining));
    setBulkMethod("orange_money");
    setBulkTransactionId("");
    setBulkNotes("");
    bulkPayModal.onOpen();
  }, [totalSelectedRemaining, bulkPayModal]);

  const closeBulkPay = useCallback(() => {
    bulkPayModal.onClose();
  }, [bulkPayModal]);

  const handleBulkPay = useCallback(async () => {
    const amount = parseFloat(bulkAmount);
    if (Number.isNaN(amount) || amount <= 0 || selectedInvoices.length === 0) {
      showToast({
        toast,
        message: "Montant invalide ou aucune facture sélectionnée.",
        status: toastTypes.error,
      });
      return;
    }
    setSubmitting(true);
    try {
      await bulkPayInvoices({
        invoice_ids: selectedInvoices.map((inv: Invoice) => inv.id),
        amount,
        method: bulkMethod,
        transaction_id: bulkTransactionId.trim() || undefined,
        notes: bulkNotes.trim() || undefined,
      });
      showToast({
        toast,
        message: "Paiement groupé enregistré avec succès.",
        status: toastTypes.success,
      });
      setSelectedIds(new Set());
      closeBulkPay();
    } catch (e: any) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.detail ||
        e?.message ||
        "Erreur lors du paiement groupé.";
      showToast({ toast, message: msg, status: toastTypes.error });
    } finally {
      setSubmitting(false);
    }
  }, [
    bulkAmount,
    bulkMethod,
    bulkTransactionId,
    bulkNotes,
    selectedInvoices,
    bulkPayInvoices,
    toast,
    closeBulkPay,
  ]);

  const toggleSelect = (id: string, payable: boolean) => {
    if (!payable) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const payables = invoices.filter((inv: Invoice) => isPayable(inv));
    const allSelected = payables.every((inv: Invoice) => selectedIds.has(inv.id));
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(payables.map((inv: Invoice) => inv.id)));
  };

  return (
    <TrackingPageLayout
      title="Factures (tous mes élèves)"
      action={
        <HStack spacing={2}>
          <Select
            placeholder="Tous les statuts"
            value={statusFilter ?? ""}
            onChange={(e) => setStatusFilter(e.target.value || undefined)}
            w="220px"
            size="sm"
          >
            <option value="pending">En attente</option>
            <option value="partially_paid">Partiellement payée</option>
            <option value="paid">Payée</option>
            <option value="overdue">En retard</option>
            <option value="cancelled">Annulée</option>
          </Select>
          <Button size="sm" onClick={() => refetch()} isLoading={isLoading}>
            Actualiser
          </Button>
          {selectedIds.size > 0 && (
            <Button size="sm" colorScheme="green" onClick={openBulkPay}>
              Paiement groupé ({selectedIds.size} facture(s))
            </Button>
          )}
        </HStack>
      }
    >
      {error && (
        <Text color="red.600" mb={3}>
          Erreur lors du chargement des factures.
        </Text>
      )}

      {isLoading ? (
        <Spinner />
      ) : invoices.length === 0 ? (
        <Text color="gray.600">Aucune facture trouvée.</Text>
      ) : (
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>
                <Checkbox
                  isChecked={
                    invoices.filter((inv: Invoice) => isPayable(inv)).length > 0 &&
                    invoices.filter((inv: Invoice) => isPayable(inv)).every((inv: Invoice) =>
                      selectedIds.has(inv.id)
                    )
                  }
                  onChange={toggleSelectAll}
                  isIndeterminate={
                    selectedIds.size > 0 &&
                    selectedIds.size < invoices.filter((inv: Invoice) => isPayable(inv)).length
                  }
                />
              </Th>
              <Th>Élève</Th>
              <Th>Montant</Th>
              <Th>Restant</Th>
              <Th>Échéance</Th>
              <Th>Statut</Th>
              <Th>Référence</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {invoices.map((inv: Invoice) => {
              const payable = isPayable(inv);
              return (
                <Tr key={inv.id}>
                  <Td>
                    <Checkbox
                      isChecked={selectedIds.has(inv.id)}
                      isDisabled={!payable}
                      onChange={() => toggleSelect(inv.id, payable)}
                    />
                  </Td>
                  <Td>{inv.entity_name ?? "—"}</Td>
                  <Td>{formatAmount(inv.amount)}</Td>
                  <Td>{formatAmount(inv.remaining_amount)}</Td>
                  <Td>{formatDate(inv.due_date)}</Td>
                  <Td>{inv.status_display ?? inv.status ?? "—"}</Td>
                  <Td>{inv.reference}</Td>
                  <Td>
                    {payable && (
                      <Button size="xs" colorScheme="green" onClick={() => openSinglePay(inv)}>
                        Payer
                      </Button>
                    )}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      )}

      {/* Modal paiement unitaire */}
      <Modal isOpen={singlePayModal.isOpen} onClose={closeSinglePay} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Payer la facture</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {payingInvoice && (
              <Box mb={3}>
                <Text fontSize="sm" color="gray.600">
                  {payingInvoice.entity_name} – Réf. {payingInvoice.reference} – Restant dû:{" "}
                  {formatAmount(payingInvoice.remaining_amount)}
                </Text>
              </Box>
            )}
            <FormControl isRequired mb={3}>
              <FormLabel>Montant (FCFA)</FormLabel>
              <Input
                type="number"
                step="0.01"
                min={0}
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                placeholder="Montant à payer"
              />
            </FormControl>
            <FormControl isRequired mb={3}>
              <FormLabel>Méthode de paiement</FormLabel>
              <Select value={payMethod} onChange={(e) => setPayMethod(e.target.value)}>
                {PAYMENT_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl mb={3}>
              <FormLabel>Référence transaction (optionnel)</FormLabel>
              <Input
                value={payTransactionId}
                onChange={(e) => setPayTransactionId(e.target.value)}
                placeholder="ID transaction fournisseur"
              />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel>Notes (optionnel)</FormLabel>
              <Input
                value={payNotes}
                onChange={(e) => setPayNotes(e.target.value)}
                placeholder="Notes"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button variant="ghost" onClick={closeSinglePay}>
                Annuler
              </Button>
              <Button
                colorScheme="green"
                onClick={handleSinglePay}
                isLoading={submitting}
                isDisabled={!payAmount || parseFloat(payAmount) <= 0}
              >
                Enregistrer le paiement
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Modal paiement groupé */}
      <Modal isOpen={bulkPayModal.isOpen} onClose={closeBulkPay} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Paiement groupé</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box mb={3}>
              <Text fontSize="sm" color="gray.600">
                {selectedInvoices.length} facture(s) sélectionnée(s). Total restant dû:{" "}
                {formatAmount(totalSelectedRemaining)}
              </Text>
            </Box>
            <FormControl isRequired mb={3}>
              <FormLabel>Montant total à répartir (FCFA)</FormLabel>
              <Input
                type="number"
                step="0.01"
                min={0}
                value={bulkAmount}
                onChange={(e) => setBulkAmount(e.target.value)}
                placeholder="Montant total"
              />
            </FormControl>
            <FormControl isRequired mb={3}>
              <FormLabel>Méthode de paiement</FormLabel>
              <Select value={bulkMethod} onChange={(e) => setBulkMethod(e.target.value)}>
                {PAYMENT_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl mb={3}>
              <FormLabel>Référence transaction (optionnel)</FormLabel>
              <Input
                value={bulkTransactionId}
                onChange={(e) => setBulkTransactionId(e.target.value)}
                placeholder="ID transaction fournisseur"
              />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel>Notes (optionnel)</FormLabel>
              <Input
                value={bulkNotes}
                onChange={(e) => setBulkNotes(e.target.value)}
                placeholder="Notes"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <HStack>
              <Button variant="ghost" onClick={closeBulkPay}>
                Annuler
              </Button>
              <Button
                colorScheme="green"
                onClick={handleBulkPay}
                isLoading={submitting}
                isDisabled={!bulkAmount || parseFloat(bulkAmount) <= 0}
              >
                Enregistrer le paiement groupé
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </TrackingPageLayout>
  );
}
